// One rule for classifying recurring budget rows — income, payroll deduction,
// savings plan — shared by the income tab and the budget-vs-actual aggregation.
//
// Never classify by subcategory slug: the slug is derived from the name the
// user typed (MikoVault crud.slugify_subcategory), so "Rente" becomes `rente`
// and "Only Fans" becomes `only_fans`. No fixed list can contain them, which is
// exactly how income lines went missing from the tab and the planned-income
// total. The structured fields budget_group / category_type are authoritative.

export const SALARY_SUBCATEGORY = 'salary';
export const CORRECTION_SUBCATEGORY = 'correction';

// The eight German payroll deductions, in payslip order, with their labels.
// This map is a sort order and label lookup — NOT a filter. A deduction the
// user creates for themselves (say Pflegeversicherung) still belongs on the
// payslip; it renders with its own name, appended after the canonical ones.
export const DEDUCTION_LABEL_KEYS = {
  income_tax: 'salary.lohnsteuer',
  solidarity_tax: 'salary.soli',
  church_tax: 'salary.kirchensteuer',
  health_insurance: 'salary.krankenversicherung',
  additional_employee_contribution: 'salary.zusatzbeitragKK',
  pension_insurance: 'salary.rentenversicherung',
  unemployment_insurance: 'salary.arbeitslosenversicherung',
  vbl_insurance: 'salary.vbl',
};

// Insertion order of string keys is guaranteed, so the label map doubles as the
// payslip order and the two cannot drift apart.
export const DEDUCTION_ORDER = Object.keys(DEDUCTION_LABEL_KEYS);

export const isIncomeRow = (row) =>
  row?.category_type === 'income' || row?.budget_group === 'income';

export const isDeductionRow = (row) => row?.budget_group === 'deductions';

/**
 * Bucket an income row: 'salary' | 'correction' | 'plannedOther'
 * | 'unplannedOther', or null when the row is not income at all.
 */
export const incomeBucket = (row) => {
  if (!isIncomeRow(row)) return null;
  if (row.subcategory === SALARY_SUBCATEGORY) return 'salary';
  if (row.subcategory === CORRECTION_SUBCATEGORY) return 'correction';
  // Counting as planned requires an explicit group. Legacy rows read straight
  // out of monthly_budgets carry no budget_group; treating them as planned
  // would silently move existing users' figures.
  return row.budget_group === 'income' ? 'plannedOther' : 'unplannedOther';
};

/** Canonical deductions first, in payslip order, then the rest by name. */
export const sortDeductions = (rows) =>
  [...(rows || [])].sort((a, b) => {
    const rankA = DEDUCTION_ORDER.indexOf(a.subcategory);
    const rankB = DEDUCTION_ORDER.indexOf(b.subcategory);
    const orderA = rankA === -1 ? DEDUCTION_ORDER.length : rankA;
    const orderB = rankB === -1 ? DEDUCTION_ORDER.length : rankB;
    if (orderA !== orderB) return orderA - orderB;
    return (a.name || '').localeCompare(b.name || '');
  });

// Which kind of income line the user is creating. The payslip looks for the
// slug `salary`; every other income line gets a slug the backend derives from
// the typed name, so it must not be guessed here.
export const incomeKindOf = (subcategory) =>
  subcategory === SALARY_SUBCATEGORY ? 'salary' : 'other';

/** Subcategory to send when creating an income line, or null to let the backend slugify. */
export const subcategoryForIncomeKind = (kind) =>
  kind === 'salary' ? SALARY_SUBCATEGORY : null;

// Sparkonto movements and stock purchases are transfers between the user's own
// accounts, not a monthly commitment. A 2024 migration removed them from the
// budget on purpose (MikoVault migrations/legacy/cleanup_depot_budget_entries.py),
// and DepotTab skips savings_account for the same reason.
export const SAVINGS_MOVEMENT_SUBCATEGORIES = ['savings_account', 'stock_purchase'];

export const isSavingsRow = (row) => row?.budget_group === 'savings';

/**
 * Does this savings row belong in the planned savings total? Every savings line
 * does, except the account movements — the slug is derived from the typed name,
 * so a whitelist of known plans would drop everything the user creates.
 */
export const isPlannedSavingsRow = (row) =>
  isSavingsRow(row) && !SAVINGS_MOVEMENT_SUBCATEGORIES.includes(row.subcategory);
